import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Flame, Play, Pause, ChevronRight, Calendar, Heart, Target, Zap, CheckCircle2, Lock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Navigation from '@/components/Navigation';
import InnerShiftCard from '@/components/InnerShiftCard';
import WaveLoader from '@/components/WaveLoader';
import { getDailyAffirmation, refreshAffirmation, getProgress, getTracks, getCategories } from '@/lib/api';
import { toast } from 'sonner';

// Mood options for check-in
const MOOD_OPTIONS = [
  { emoji: '😊', label: 'Great', color: 'bg-green-500' },
  { emoji: '🙂', label: 'Good', color: 'bg-teal-500' },
  { emoji: '😐', label: 'Okay', color: 'bg-yellow-500' },
  { emoji: '😔', label: 'Low', color: 'bg-orange-500' },
  { emoji: '😢', label: 'Struggling', color: 'bg-red-500' },
];

// Goal options
const GOAL_OPTIONS = [
  'Build confidence',
  'Reduce stress',
  'Improve sleep',
  'Find inner peace',
  'Boost energy',
  'Practice gratitude',
  'Overcome fear',
  'Attract abundance',
];

// Action options (mood-based commitment)
const ACTION_OPTIONS = [
  { value: 'yes', label: 'Yes, I will do it!', emoji: '💪' },
  { value: 'ready', label: "I'm ready to grow!", emoji: '🌱' },
  { value: 'committed', label: 'Fully committed today!', emoji: '🔥' },
  { value: 'peaceful', label: 'Finding my peace today', emoji: '🧘' },
  { value: 'grateful', label: 'Grateful for this moment', emoji: '🙏' },
];

export default function DashboardPage() {
  const [affirmation, setAffirmation] = useState(null);
  const [affirmationLoading, setAffirmationLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // New state for dashboard features
  const [selectedMood, setSelectedMood] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const [checkIns, setCheckIns] = useState([]);

  // Wealth Activation audio player
  const wealthAudioRef = useRef(null);
  const [wealthPlaying, setWealthPlaying] = useState(false);
  const [wealthProgress, setWealthProgress] = useState(0);
  const [wealthCurrentTime, setWealthCurrentTime] = useState(0);
  const [wealthDuration, setWealthDuration] = useState(0);
  const [wealthDialogue, setWealthDialogue] = useState('');
  const [wealthTrackUrl, setWealthTrackUrl] = useState('/audio/wealth_activation.mp3');
  const [wealthScript, setWealthScript] = useState([]);

  // Get today's date formatted
  const today = new Date();
  const formattedDate = today.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  useEffect(() => {
    loadData();
    // Load saved check-ins from localStorage
    const savedCheckIns = JSON.parse(localStorage.getItem('tuneawaken_checkins') || '[]');
    setCheckIns(savedCheckIns);
    
    // Load today's selections
    const todayKey = today.toISOString().split('T')[0];
    const todayData = JSON.parse(localStorage.getItem(`tuneawaken_today_${todayKey}`) || '{}');
    if (todayData.mood !== undefined) setSelectedMood(todayData.mood);
    if (todayData.goal) setSelectedGoal(todayData.goal);
    if (todayData.action) setSelectedAction(todayData.action);
  }, []);

  const loadData = async () => {
    try {
      const [affRes, progRes, tracksRes, catRes, settingsRes] = await Promise.all([
        getDailyAffirmation(),
        getProgress(),
        getTracks(),
        getCategories(),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`).then(r => r.json()).catch(() => null),
      ]);

      setAffirmation(affRes.data);
      setProgress(progRes.data);
      setTracks(tracksRes.data.slice(0, 4));
      setCategories(catRes.data.slice(0, 4));
      if (settingsRes?.wealth_activation_track) {
        setWealthTrackUrl(settingsRes.wealth_activation_track);
        // Fetch subtitles for this track from backend
        try {
          const subsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subtitles?track_id=${encodeURIComponent(settingsRes.wealth_activation_track)}`).then(r => r.json());
          if (subsRes?.lines?.length > 0) {
            setWealthScript(subsRes.lines);
          } else {
            // Fallback: check localStorage (subtitle editor saves here too)
            const localData = JSON.parse(localStorage.getItem('subtitle_editor_data') || '{}');
            // Search all saved tracks for matching audio file
            for (const [key, val] of Object.entries(localData)) {
              if (val?.lines?.length > 0) {
                // Check if this key matches the track file
                const trackFile = settingsRes.wealth_activation_track;
                const fileName = trackFile.split('/').pop().replace('.mp3', '');
                if (key.includes(fileName) || key === trackFile) {
                  setWealthScript(val.lines);
                  break;
                }
              }
            }
          }
        } catch {}
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshAffirmation = async () => {
    setAffirmationLoading(true);
    try {
      const res = await refreshAffirmation();
      setAffirmation(res.data);
    } catch {
      toast.error('Failed to generate new affirmation');
    } finally {
      setAffirmationLoading(false);
    }
  };

  const handleMoodSelect = (index) => {
    setSelectedMood(index);
    const todayKey = today.toISOString().split('T')[0];
    
    // Save to localStorage
    const todayData = JSON.parse(localStorage.getItem(`tuneawaken_today_${todayKey}`) || '{}');
    todayData.mood = index;
    localStorage.setItem(`tuneawaken_today_${todayKey}`, JSON.stringify(todayData));
    
    // Add to check-ins if not already checked in today
    const existingCheckIn = checkIns.find(c => c.date === todayKey);
    if (!existingCheckIn) {
      const newCheckIns = [...checkIns, { date: todayKey, mood: index }];
      setCheckIns(newCheckIns);
      localStorage.setItem('tuneawaken_checkins', JSON.stringify(newCheckIns));
    } else {
      const updatedCheckIns = checkIns.map(c => 
        c.date === todayKey ? { ...c, mood: index } : c
      );
      setCheckIns(updatedCheckIns);
      localStorage.setItem('tuneawaken_checkins', JSON.stringify(updatedCheckIns));
    }
    
    toast.success(`Mood logged: ${MOOD_OPTIONS[index].label}`);
  };

  const handleGoalChange = (value) => {
    setSelectedGoal(value);
    const todayKey = today.toISOString().split('T')[0];
    const todayData = JSON.parse(localStorage.getItem(`tuneawaken_today_${todayKey}`) || '{}');
    todayData.goal = value;
    localStorage.setItem(`tuneawaken_today_${todayKey}`, JSON.stringify(todayData));
  };

  const handleActionChange = (value) => {
    setSelectedAction(value);
    const todayKey = today.toISOString().split('T')[0];
    const todayData = JSON.parse(localStorage.getItem(`tuneawaken_today_${todayKey}`) || '{}');
    todayData.action = value;
    localStorage.setItem(`tuneawaken_today_${todayKey}`, JSON.stringify(todayData));
    
    const action = ACTION_OPTIONS.find(a => a.value === value);
    if (action) {
      toast.success(`${action.emoji} ${action.label}`);
    }
  };

  const toggleWealthAudio = () => {
    const audio = wealthAudioRef.current;
    if (!audio) return;
    if (wealthPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setWealthPlaying(!wealthPlaying);
  };

  const handleWealthTimeUpdate = () => {
    const audio = wealthAudioRef.current;
    if (!audio) return;
    const currentTime = audio.currentTime;
    setWealthCurrentTime(currentTime);
    setWealthDuration(audio.duration || 0);
    setWealthProgress(audio.duration ? (currentTime / audio.duration) * 100 : 0);
    
    const line = wealthScript.find(
      (entry) => currentTime >= entry.start && currentTime < entry.end
    );
    setWealthDialogue(line ? line.text : '');
  };

  const handleWealthSeek = (e) => {
    const audio = wealthAudioRef.current;
    if (!audio || !audio.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    audio.currentTime = pct * audio.duration;
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return <WaveLoader />;
  }

  return (
    <div className="min-h-screen bg-zinc-950 pb-24 md:pb-8 md:pt-24">
      <Navigation />

      <main className="container mx-auto px-4 md:px-6 py-8">
        {/* Header with Date */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-zinc-500 text-sm mb-2">
            <Calendar className="w-4 h-4" />
            <span data-testid="today-date">{formattedDate}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-serif text-white">Your Daily Practice</h1>
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

          {/* 21-Day Inner Shift — hero card */}
          <InnerShiftCard />

          {/* Daily Affirmation Card */}
          <div 
            className="col-span-1 md:col-span-2 bg-gradient-to-br from-violet-900/30 via-zinc-900 to-zinc-950 rounded-2xl border border-violet-500/20 p-6 md:p-8 relative overflow-hidden"
            data-testid="affirmation-card"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-violet-600/10 rounded-full blur-3xl" />
            
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-violet-400" />
                  <span className="text-sm text-violet-400 font-medium">Today's Affirmation</span>
                </div>
                <button
                  onClick={handleRefreshAffirmation}
                  disabled={affirmationLoading}
                  className="p-2 rounded-full hover:bg-violet-500/10 transition-colors group"
                  title="New Affirmation"
                  data-testid="refresh-affirmation-btn"
                >
                  <RefreshCw className={`w-4 h-4 text-violet-400 group-hover:text-violet-300 transition-all ${affirmationLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              
              <blockquote className={`text-xl md:text-2xl lg:text-3xl font-serif text-white leading-relaxed transition-opacity duration-300 ${affirmationLoading ? 'opacity-40' : 'opacity-100'}`}>
                "{affirmation?.text || 'Loading...'}"
              </blockquote>
            </div>
          </div>

          {/* How Are You Feeling Today - Mood Check-in */}
          <div 
            className="col-span-1 md:col-span-2 bg-gradient-to-br from-amber-900/20 via-zinc-900 to-zinc-950 rounded-2xl border border-amber-500/20 p-6 relative overflow-hidden"
            data-testid="wealth-activation-card"
          >
            <video
              key={wealthTrackUrl}
              ref={wealthAudioRef}
              src={wealthTrackUrl}
              onTimeUpdate={handleWealthTimeUpdate}
              onEnded={() => { setWealthPlaying(false); setWealthProgress(0); setWealthCurrentTime(0); setWealthDialogue(''); }}
              onLoadedMetadata={handleWealthTimeUpdate}
              preload="metadata"
              playsInline
              style={{position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none'}}
            />
            <div className="absolute top-0 right-0 w-48 h-48 bg-amber-600/10 rounded-full blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-5 h-5 text-amber-400" />
                <span className="text-sm text-amber-400 font-medium">Wealth Activation</span>
              </div>
              <p className="text-zinc-400 text-sm mb-5">Activate your wealth frequency. Close your eyes, listen, and align.</p>
              
              {/* Dialogue display */}
              <div className="min-h-[48px] mb-4 flex items-center justify-center">
                {wealthDialogue && (
                  <p 
                    key={wealthDialogue}
                    className="text-white text-center text-lg font-serif italic animate-subtitle-text"
                    data-testid="wealth-dialogue-text"
                  >
                    {wealthDialogue}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-4">
                <button
                  onClick={toggleWealthAudio}
                  className="w-14 h-14 rounded-full bg-amber-500 hover:bg-amber-400 flex items-center justify-center shadow-lg shadow-amber-500/20 transition-all hover:scale-105"
                  data-testid="wealth-activation-play-btn"
                >
                  {wealthPlaying 
                    ? <Pause className="w-6 h-6 text-zinc-900" fill="currentColor" />
                    : <Play className="w-6 h-6 text-zinc-900 ml-0.5" fill="currentColor" />
                  }
                </button>
                <div className="flex-1">
                  <div 
                    className="w-full h-2 bg-white/10 rounded-full cursor-pointer overflow-hidden"
                    onClick={handleWealthSeek}
                    data-testid="wealth-activation-progress"
                  >
                    <div 
                      className="h-full bg-amber-400 rounded-full transition-all duration-200"
                      style={{ width: `${wealthProgress}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1.5">
                    <span className="text-xs text-zinc-500">{formatTime(wealthCurrentTime)}</span>
                    <span className="text-xs text-zinc-500">{wealthDuration ? formatTime(wealthDuration) : '5:42'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* How Are You Feeling Today - Mood Check-in */}
          <div 
            className="col-span-1 md:col-span-2 bg-zinc-900/50 rounded-2xl border border-white/5 p-6"
            data-testid="mood-checkin-card"
          >
            <div className="flex items-center gap-2 mb-4">
              <Heart className="w-5 h-5 text-pink-400" />
              <span className="text-sm text-pink-400 font-medium">How are you feeling today?</span>
            </div>
            
            <div className="flex flex-wrap gap-3 mb-4">
              {MOOD_OPTIONS.map((mood, index) => (
                <button
                  key={mood.label}
                  onClick={() => handleMoodSelect(index)}
                  className={`flex flex-col items-center p-3 rounded-xl transition-all ${
                    selectedMood === index 
                      ? 'bg-white/10 ring-2 ring-pink-400 scale-105' 
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                  data-testid={`mood-${mood.label.toLowerCase()}`}
                >
                  <span className="text-2xl mb-1">{mood.emoji}</span>
                  <span className="text-xs text-zinc-400">{mood.label}</span>
                </button>
              ))}
            </div>
            
            <div className="flex items-center gap-2 text-xs text-zinc-600">
              <CheckCircle2 className="w-3 h-3" />
              <span>{checkIns.length} check-ins this year</span>
            </div>
          </div>

          {/* My Goals Today */}
          <div 
            className="bg-zinc-900/50 rounded-2xl border border-white/5 p-6"
            data-testid="goals-card"
          >
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-5 h-5 text-teal-400" />
              <span className="text-sm text-teal-400 font-medium">My Goal Today</span>
            </div>
            
            <Select value={selectedGoal} onValueChange={handleGoalChange}>
              <SelectTrigger className="w-full bg-white/5 border-white/10 text-white rounded-xl">
                <SelectValue placeholder="Select your goal..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {GOAL_OPTIONS.map((goal) => (
                  <SelectItem 
                    key={goal} 
                    value={goal}
                    className="text-zinc-300 focus:bg-white/10 focus:text-white"
                  >
                    {goal}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedGoal && (
              <p className="mt-3 text-sm text-zinc-500">
                Focus on: <span className="text-teal-400">{selectedGoal}</span>
              </p>
            )}
          </div>

          {/* Actions - Commitment Dropdown */}
          <div 
            className="bg-zinc-900/50 rounded-2xl border border-white/5 p-6"
            data-testid="actions-card"
          >
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-amber-400" />
              <span className="text-sm text-amber-400 font-medium">My Commitment</span>
            </div>
            
            <Select value={selectedAction} onValueChange={handleActionChange}>
              <SelectTrigger className="w-full bg-white/5 border-white/10 text-white rounded-xl">
                <SelectValue placeholder="Choose your action..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {ACTION_OPTIONS.map((action) => (
                  <SelectItem 
                    key={action.value} 
                    value={action.value}
                    className="text-zinc-300 focus:bg-white/10 focus:text-white"
                  >
                    {action.emoji} {action.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedAction && (
              <p className="mt-3 text-sm text-zinc-500">
                {ACTION_OPTIONS.find(a => a.value === selectedAction)?.emoji} You've got this!
              </p>
            )}
          </div>

          {/* Streak Card */}
          <div 
            className="bg-gradient-to-br from-amber-900/20 via-zinc-900 to-zinc-950 rounded-2xl border border-amber-500/20 p-6 flex flex-col justify-between"
            data-testid="streak-card"
          >
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-amber-400" />
              <span className="text-sm text-amber-400 font-medium">Current Streak</span>
            </div>
            
            <div className="my-4">
              <span className="text-5xl font-serif text-white">{progress?.current_streak || 0}</span>
              <span className="text-2xl text-zinc-500 ml-1">days</span>
            </div>
            
            <p className="text-xs text-zinc-600">
              Longest: {progress?.longest_streak || 0} days
            </p>
          </div>

          {/* Quick Stats Card */}
          <div 
            className="bg-zinc-900/50 rounded-2xl border border-white/5 p-6 flex flex-col justify-between"
            data-testid="stats-card"
          >
            <span className="text-sm text-zinc-500 font-medium">Total Sessions</span>
            <span className="text-4xl font-serif text-white my-2">{progress?.total_sessions || 0}</span>
            <span className="text-xs text-zinc-600">
              {Math.round((progress?.total_minutes || 0) / 60)} hours of mindfulness
            </span>
          </div>

          {/* Featured Track Card */}
          {tracks.length > 0 && (
            <Link
              to={`/player/${tracks[0].id}`}
              className="col-span-1 md:col-span-2 relative rounded-2xl overflow-hidden border border-white/5 hover:border-white/10 transition-colors group h-48"
              data-testid="featured-track"
            >
              <img 
                src={tracks[0].image_url}
                alt={tracks[0].title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/60 to-transparent" />
              
              <div className="absolute inset-0 flex flex-col justify-end p-6">
                <span className="text-xs text-teal-400 uppercase tracking-wide mb-2">Featured</span>
                <h3 className="text-xl font-serif text-white mb-1">{tracks[0].title}</h3>
                <p className="text-sm text-zinc-400 line-clamp-1">{tracks[0].description}</p>
                
                <div className="flex items-center gap-3 mt-4">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Play className="w-5 h-5 text-zinc-900 ml-0.5" fill="currentColor" />
                  </div>
                  <span className="text-sm text-zinc-400">
                    {Math.floor(tracks[0].duration / 60)} min
                  </span>
                </div>
              </div>
            </Link>
          )}
        </div>

        {/* Quick Categories */}
        <section className="mt-12" data-testid="categories-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-serif text-white">Explore Categories</h2>
            <Link to="/library" className="text-sm text-zinc-500 hover:text-zinc-300 flex items-center gap-1">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((category) => {
              const isActive = category.active;
              const Wrapper = isActive ? Link : 'div';
              const wrapperProps = isActive ? { to: `/library/${category.slug}` } : {};
              return (
                <Wrapper
                  key={category.slug}
                  {...wrapperProps}
                  className={`relative h-32 rounded-xl overflow-hidden group ${isActive ? '' : 'cursor-default'}`}
                  data-testid={`quick-category-${category.slug}`}
                >
                  <img 
                    src={category.image_url}
                    alt={category.name}
                    className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 ${isActive ? 'group-hover:scale-110' : 'grayscale-[40%]'}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/90 to-zinc-900/30" />
                  {!isActive && (
                    <div className="absolute top-2 right-2 z-20 flex items-center gap-1 bg-zinc-900/80 backdrop-blur-sm border border-white/10 rounded-full px-2.5 py-1">
                      <Lock className="w-3 h-3 text-amber-400" />
                      <span className="text-[10px] font-medium text-amber-400">Coming Soon</span>
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-end p-4">
                    <span className="text-white font-medium">{category.name}</span>
                  </div>
                </Wrapper>
              );
            })}
          </div>
        </section>

      </main>
    </div>
  );
}
